/**
 * Einziger Pinia-Store der Anwendung: der angemeldete Nutzer.
 * Serverdaten liegen sonst in TanStack Query.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { me as fetchMe } from '@/api/auth'
import { ApiError } from '@/api/http'
import type { LicenseSummary, MeResponse, User } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const license = ref<LicenseSummary | null>(null)
  /** `true`, sobald `/auth/me` einmal beantwortet wurde. */
  const resolved = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const username = computed(() => user.value?.username ?? '')

  function setUser(next: MeResponse | User | null): void {
    if (next === null) {
      user.value = null
      license.value = null
    } else {
      const { id, username: name, role, must_change_password } = next
      user.value = { id, username: name, role, must_change_password }
      license.value = 'license' in next ? (next.license ?? null) : license.value
    }
    resolved.value = true
  }

  function setLicense(next: LicenseSummary | null): void {
    license.value = next
  }

  function clear(): void {
    user.value = null
    license.value = null
    resolved.value = true
  }

  /**
   * Holt `/auth/me` genau einmal (parallele Aufrufe teilen sich die
   * laufende Anfrage). `force` erzwingt eine erneute Abfrage.
   */
  let inflight: Promise<User | null> | null = null

  async function ensureResolved(force = false): Promise<User | null> {
    if (!force && resolved.value) return user.value
    if (!force && inflight) return inflight

    inflight = (async () => {
      try {
        const data = await fetchMe()
        setUser(data)
      } catch (error) {
        // 401 = nicht angemeldet; alles andere ebenfalls als „abgemeldet“
        // behandeln, damit der Guard nicht hängen bleibt.
        if (!(error instanceof ApiError)) throw error
        clear()
      } finally {
        inflight = null
      }
      return user.value
    })()

    return inflight
  }

  return {
    user,
    license,
    resolved,
    isAuthenticated,
    isAdmin,
    username,
    setUser,
    setLicense,
    clear,
    ensureResolved,
  }
})

export type AuthStore = ReturnType<typeof useAuthStore>
